import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export interface ConfirmationDialogData {
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  isDanger?: boolean;
  requireTextConfirmation?: boolean; // For "DELETE" typing
  confirmationWord?: string; // Word to type (e.g., "DELETE")
  showReasonField?: boolean;
  reasonLabel?: string;
  reasonRequired?: boolean;
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  template: `
    <h2 mat-dialog-title [class.danger-title]="data.isDanger">
      <mat-icon>{{ data.isDanger ? 'error' : 'warning' }}</mat-icon>
      {{ data.title }}
    </h2>

    <mat-dialog-content>
      <p class="message">{{ data.message }}</p>

      @if(data.requireTextConfirmation) {
        <div class="danger-warning">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>WARNING: This action cannot be undone!</strong>
          </div>
        </div>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Type "{{ data.confirmationWord }}" to confirm</mat-label>
          <input matInput
                 [(ngModel)]="textConfirmation"
                 [placeholder]="data.confirmationWord || 'DELETE'"
                 required>
        </mat-form-field>
      }

      @if(data.showReasonField) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ data.reasonLabel || 'Reason' }}</mat-label>
          <textarea matInput
                    [(ngModel)]="reason"
                    rows="3"
                    [placeholder]="data.reasonLabel || 'Provide a reason...'"
                    [required]="data.reasonRequired ?? false"></textarea>
        </mat-form-field>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">
        {{ data.cancelText || 'Cancel' }}
      </button>
      <button mat-flat-button
              [color]="data.isDanger ? 'warn' : 'primary'"
              (click)="onConfirm()"
              [disabled]="!isValid()">
        {{ data.confirmText }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #ff9800;
    }

    h2.danger-title {
      color: #f44336;
    }

    .message {
      margin: 16px 0;
      line-height: 1.6;
    }

    .danger-warning {
      display: flex;
      gap: 12px;
      padding: 12px;
      background: #ffebee;
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .danger-warning mat-icon {
      color: #f44336;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .danger-warning strong {
      color: #f44336;
    }

    .full-width {
      width: 100%;
      margin-top: 8px;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
    }
  `]
})
export class ConfirmationDialogComponent {
  textConfirmation = '';
  reason = '';

  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationDialogData
  ) {}

  isValid(): boolean {
    if (this.data.requireTextConfirmation) {
      if (this.textConfirmation !== this.data.confirmationWord) {
        return false;
      }
    }

    if (this.data.showReasonField && this.data.reasonRequired) {
      if (!this.reason.trim()) {
        return false;
      }
    }

    return true;
  }

  onConfirm() {
    this.dialogRef.close({
      confirmed: true,
      reason: this.reason.trim() || undefined
    });
  }

  onCancel() {
    this.dialogRef.close({ confirmed: false });
  }
}
